import type { RequiredEnrollmentDocument } from '@/api/enrollment-required-documents'
import type { EnrollmentDocumentType } from '@/api/types/enrollment'

export type UploadFieldKey = string

export type UploadConfig = {
  key: UploadFieldKey
  title: string
  description: string
  documentType: EnrollmentDocumentType
  uploadEmptyLabel: string
  helperTitle?: string
  helperDescription?: string
  helperButtonLabel?: string
  helperTone?: 'blue' | 'yellow'
  notes?: string
}

export const helperToneClasses = {
  blue: 'bg-blue-50 border-blue-200 text-blue-900 text-blue-700',
  yellow: 'bg-yellow-50 border-yellow-300 text-yellow-900 text-yellow-700'
}

const MVR_URLS_BY_STATE: Record<string, string> = {
  AL: 'https://www.alabamadmv.org/services/driving-record/',
  AK: 'https://doa.alaska.gov/dmv/akuse/drivrecord.htm',
  AZ: 'https://azmvdnow.gov/home',
  AR: 'https://www.dfa.arkansas.gov/drivers-licenses/driver-record-requests/',
  CA: 'https://www.dmv.ca.gov/portal/driver-education-and-safety/educational-materials/sample-documents/driving-record/',
  CO: 'https://dmv.colorado.gov/driving-record',
  CT: 'https://portal.ct.gov/DMV/License-Credentials/Driving-Records/Driving-Records',
  DE: 'https://dmv.de.gov/services/OnlineServices/drivingRecords/command-DrivingRecord.shtml',
  FL: 'https://www.flhsmv.gov/driver-licenses-id-cards/driving-records/',
  GA: 'https://emv.dds.ga.gov/',
  HI: 'https://hidot.hawaii.gov/highways/home/motor-vehicle-safety-office/',
  ID: 'https://itd.idaho.gov/dmv/driverservices/driverrecords/',
  IL: 'https://cyberdriveillinois.com/facilities/facilitylist.html',
  IN: 'https://www.in.gov/bmv/driving-records/',
  IA: 'https://iamvd.iowa.gov/IamVD/',
  KS: 'https://www.ksrevenue.gov/dovdrivingrecord.html',
  KY: 'https://drive.ky.gov/driver-licensing/Pages/Driving-History-Abstract.aspx',
  LA: 'https://dpsmv.la.gov/node/178',
  ME: 'https://www.maine.gov/sos/bmv/licenses/record.html',
  MD: 'https://mva.maryland.gov/drivers/Pages/driving-records.aspx',
  MA: 'https://www.mass.gov/how-to/get-a-copy-of-your-driving-record',
  MI: 'https://www.michigan.gov/sos/vehicle-services/titles-registration/driving-record',
  MN: 'https://dps.mn.gov/divisions/dvs/Pages/driving-record.aspx',
  MS: 'https://www.dps.ms.gov/driver-service/driver-record',
  MO: 'https://dor.mo.gov/driver-license/motorists/driving-record/',
  MT: 'https://dojmt.gov/driving/driver-records/',
  NE: 'https://dmv.nebraska.gov/dl/request-your-driving-record',
  NV: 'https://dmv.nv.gov/Drivers/ObtainDrivingRecord/',
  NH: 'https://www.nh.gov/safety/divisions/dmv/driver-licensing/records/',
  NJ: 'https://www.nj.gov/mvc/license/abstract.htm',
  NM: 'https://www.mvd.newmexico.gov/driver-authorization/requesting-your-driving-record/',
  NY: 'https://dmv.ny.gov/driver-license/get-your-driving-record',
  NC: 'https://www.ncdot.gov/dmv/driver-vehicle/driving-records/',
  ND: 'https://www.dot.nd.gov/divisions/mv/driverrecords.htm',
  OH: 'https://www.bmv.ohio.gov/dl-record.aspx',
  OK: 'https://www.ok.gov/dps/Driving_Records/',
  OR: 'https://www.oregon.gov/odot/dmv/pages/driverid/driveRecord.aspx',
  PA: 'https://www.dot.state.pa.us/Public/DVSHome',
  RI: 'https://dmv.ri.gov/licenses-permits-ids/driver-records',
  SC: 'https://www.scdmvonline.com/Driver-Services/Driving-Records',
  SD: 'https://dps.sd.gov/driver-licensing/driver-records',
  TN: 'https://www.tn.gov/safety/driver-services/driver-records.html',
  TX: 'https://www.txdmv.gov/motorists/online-services',
  UT: 'https://dld.utah.gov/services/request-a-driving-record/',
  VT: 'https://dmv.vermont.gov/licenses/requesting-your-driving-record',
  VA: 'https://www.dmv.virginia.gov/records/personal-records',
  WA: 'https://www.dol.wa.gov/driver-licenses-and-permits/driving-records',
  WV: 'https://transportation.wv.gov/DMV/Pages/drivingrequests.aspx',
  WI: 'https://wisconsindot.gov/Pages/dmv/license-drvs/driving-record/abstract.aspx',
  WY: 'https://www.dot.state.wy.us/home/driver_license_records/driving_records.html'
}

const uploadConfigsByType: Partial<Record<EnrollmentDocumentType, UploadConfig>> = {
  DRIVERS_LICENSE: {
    key: 'driverLicense',
    title: "Driver's License",
    description: "Upload a clear photo or scan of your driver's license.",
    documentType: 'DRIVERS_LICENSE',
    uploadEmptyLabel: '+ Upload File'
  },
  OTHER: {
    key: 'mvr',
    title: 'Motor Vehicle Report (MVR)',
    description: 'Your Motor Vehicle Report helps us verify your driving history.',
    documentType: 'OTHER',
    uploadEmptyLabel: '+ Upload File',
    helperTitle: "Don't have MVR?",
    helperDescription: "Obtain it from your state's motor vehicle website.",
    helperButtonLabel: 'Get MVR',
    helperTone: 'blue',
    notes: 'MVR'
  },
  HIGH_SCHOOL_DIPLOMA: {
    key: 'diploma',
    title: 'High School Diploma or GED',
    description: 'Upload a copy of your High School Diploma or GED Certificate.',
    documentType: 'HIGH_SCHOOL_DIPLOMA',
    uploadEmptyLabel: '+ Upload File',
    helperTitle: "Don't have a Diploma or GED?",
    helperDescription: 'You can obtain the ABT Test by clicking here.',
    helperButtonLabel: 'Schedule ABT Test',
    helperTone: 'yellow'
  },
  GED: {
    key: 'diploma',
    title: 'High School Diploma or GED',
    description: 'Upload a copy of your High School Diploma or GED Certificate.',
    documentType: 'GED',
    uploadEmptyLabel: '+ Upload File',
    helperTitle: "Don't have a Diploma or GED?",
    helperDescription: 'You can obtain the ABT Test by clicking here.',
    helperButtonLabel: 'Schedule ABT Test',
    helperTone: 'yellow'
  },
  SOCIAL_SECURITY: {
    key: 'ssn',
    title: 'Social Security Card',
    description: 'Upload a copy of your Social Security Card.',
    documentType: 'SOCIAL_SECURITY',
    uploadEmptyLabel: '+ Upload File'
  },
  MEDICAL_CERTIFICATE: {
    key: 'medicalCertificate',
    title: 'Medical Card',
    description: 'Upload a clear photo or scan of your current medical card.',
    documentType: 'MEDICAL_CERTIFICATE',
    uploadEmptyLabel: '+ Upload File'
  },
  LEARNERS_PERMIT: {
    key: 'learnersPermit',
    title: "Learner's Permit",
    description: "Upload a clear photo or scan of your learner's permit.",
    documentType: 'LEARNERS_PERMIT',
    uploadEmptyLabel: '+ Upload File'
  },
  DRUG_SCREEN: {
    key: 'drugScreen',
    title: 'Drug Screen',
    description: 'Upload your drug screen document.',
    documentType: 'DRUG_SCREEN',
    uploadEmptyLabel: '+ Upload File'
  }
}

export const getUploadConfig = (document: RequiredEnrollmentDocument): UploadConfig => {
  const configured = uploadConfigsByType[document.documentType]
  if (configured) {
    return configured
  }

  return {
    key: `document-${document.code}`,
    title: document.name,
    description: document.description ?? document.instructions ?? 'Upload this required document.',
    documentType: document.documentType,
    uploadEmptyLabel: '+ Upload File'
  }
}

export const formatAcceptedFormats = (formats?: string | null) => {
  if (!formats) {
    return '.pdf,.jpg,.jpeg,.png'
  }

  return formats
    .split(',')
    .map(format => format.trim())
    .filter(Boolean)
    .map(format => (format.startsWith('.') ? format : `.${format}`))
    .join(',')
}

export const getMvrUrl = (state: string) => {
  return MVR_URLS_BY_STATE[state] ?? 'https://www.dmv.org/driving-record.php'
}
